import React, { ReactNode, RefObject, useEffect, useMemo, useRef, useState } from 'react';

import { createFocusTrap, FocusTargetValueOrFalse, FocusTrap } from 'focus-trap';
import { useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '../../../contexts/AppContext/AppContext';
import Portal from '../../atoms/Portal/Portal';
import Btn, { BtnModifier } from '../../molecules/Btn';

// enums
import { Icons } from '../../atoms/SVG';
import { TooltipPlacement } from '../../atoms/Tooltip';

import { Namespace } from '../../../translation/namespace';
import { ModalMutationObserver } from './ModalMutationObserver';
import { GlobalDocumentIds } from '../../../types/GlobalDocumentIds';
import bem from '../../../lib/bemUtils/bem';
import { getFocusableElements } from '../../../lib/DomUtils/utils';
import ModalContext from './ModalContext';
import { sendLiveRegionMessage } from '../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { ModalDialogModifier } from './ModalDialog.modifier';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { KeyEvent } from '../../../types/KeyEvent';

export const ModalDialogBodyNoScrolling = 'modal-dialog__wrapper--body-no-scrolling';

type ModalDialogProps = {
  closeModal(): void;
  headline: ReactNode;
  initialFocusIndex?: number;
  focusOnReturn?:
    | RefObject<HTMLElement | HTMLButtonElement | undefined | null>
    | ((prevFocus: HTMLElement | SVGElement) => FocusTargetValueOrFalse);
  modifier?: Modifier<ModalDialogModifier>;
  children: ReactNode | ((ref: RefObject<HTMLElement | null>) => ReactNode);
};

export default function ModalDialog({
  closeModal,
  headline,
  children,
  modifier = undefined,
  initialFocusIndex = undefined,
  focusOnReturn,
}: ModalDialogProps) {
  const location = useLocation();
  const modalRef = useRef<HTMLDivElement>(null);
  const focusRef = useRef<HTMLElement>(null);
  const { t } = useTranslation(Namespace.ModalDialog);

  const { resetAccessKeys, disableAccessKeys } = useAppContext();
  const [returnFocusOnUnmount, setReturnFocusOnUnmount] = useState(true);
  const [preventClose, setPreventClose] = useState(false);
  const [closePreventedMessage, setClosePreventedMessage] = useState<string | null>(null);

  const idModalHeadline = 'modal-dialog-label';

  const attemptClose = () => {
    if (preventClose) {
      setClosePreventedMessage(t(`closingModalDialogBlocked`));
    } else {
      closeModal();
      sendLiveRegionMessage(t(`modalDialogWasClosed`));
    }
  };

  const getInitialFocusItem = () => {
    if (focusRef.current) {
      return focusRef.current;
    }
    if (initialFocusIndex && modalRef.current) {
      return getFocusableElements(modalRef.current)[initialFocusIndex];
    }
    // by default (undefined) focus-trap will target the first tabbable element:
    return undefined;
  };

  const getAppWrapper = () => {
    return document.getElementById(GlobalDocumentIds.genericPageTemplate);
  };

  /**
   * - Hiding the main document from screen readers via aria-hidden="true"
   * - Disabling the scrolling on <body> for all users to prevent moving dialog
   */
  const hideMainContent = () => {
    document.body.classList.add(ModalDialogBodyNoScrolling);
    const appWrapper = getAppWrapper();
    if (appWrapper) {
      appWrapper.setAttribute('aria-hidden', 'true');
    }
  };

  const showMainContent = () => {
    document.body.classList.remove(ModalDialogBodyNoScrolling);
    const appWrapper = getAppWrapper();
    if (appWrapper) {
      appWrapper.removeAttribute('aria-hidden');
    }
  };

  // component did mount
  useEffect(() => {
    let focusTrap = null as FocusTrap | null;
    let modalMutationObserver = null as ModalMutationObserver | null;
    if (modalRef && modalRef.current) {
      focusTrap = createFocusTrap(modalRef.current, {
        escapeDeactivates: false,
        initialFocus: getInitialFocusItem(),
        setReturnFocus: (prevFocus) => {
          if (!returnFocusOnUnmount) {
            return false;
          }

          if (typeof focusOnReturn === 'function') {
            return focusOnReturn(prevFocus);
          }

          if (document.contains(prevFocus)) {
            return prevFocus;
          }

          if (focusOnReturn?.current) {
            return focusOnReturn.current;
          }

          return false;
        },
      });
      focusTrap.activate();
      modalMutationObserver = new ModalMutationObserver(modalRef.current);

      disableAccessKeys();
    }
    hideMainContent();
    // component will unmount
    return () => {
      focusTrap?.deactivate({ returnFocus: returnFocusOnUnmount });
      modalMutationObserver?.disconnect();
      showMainContent();
      resetAccessKeys();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * changing location usually means
   *  - the modal will be closed
   *  - it should not return focus to the element it came from.
   *
   *  set state and handle correct closing in unmount.
   *  don't close modal here as this is to early.
   * */
  useEffect(() => {
    setReturnFocusOnUnmount(false);
  }, [location.key]);

  /**
   * Please keep this stuff on keydown!
   * Otherwise it is not possible to close a select or datepicker
   * without closing the dialog itself!
   * See SIBZIEL-8278
   * @param event
   */
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.code === KeyEvent.Escape) {
      attemptClose();
    }
  };

  const modalContextValue = useMemo(
    () => ({
      preventModalClose: setPreventClose,
      closePreventedMessage,
      closeModal,
    }),
    [setPreventClose, closePreventedMessage, closeModal],
  );

  /**
   * Popup modifier indicates that this is a popup.
   * BUT: it can have more modifiers (= array).
   * @returns {boolean}
   */
  const isPopUp = () => {
    if (Array.isArray(modifier) && modifier.length > 0) {
      if (modifier.includes(ModalDialogModifier.POP_UP)) return true;
    }
    return modifier === ModalDialogModifier.POP_UP;
  };

  return (
    <Portal>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className={isPopUp() ? 'modal-dialog__wrapper modal-dialog__wrapper--pop-up' : 'modal-dialog__wrapper'}
        // CAUTION: Correct event is keyDown here!
        // Otherwise you can't close a select or datepicker in the modal
        // dialog without closing the dialog itself. See SIBZIEL-8278
        onKeyDown={onKeyDown}
      >
        <div
          className={bem('modal-dialog', modifier)}
          aria-hidden="false"
          aria-modal="true"
          aria-labelledby={idModalHeadline}
          role="dialog"
          id="modal-dialog"
          ref={modalRef}
        >
          <div className="modal-dialog__document" role="document">
            <div className="modal-dialog__header">
              <h2 className="modal-dialog__headline" id={idModalHeadline}>
                {headline}
              </h2>
              <div className="modal-dialog__close-top">
                <Btn
                  text={t(`${Namespace.phrases}:close`)}
                  titleText={t(`${Namespace.phrases}:close`)}
                  hiddenText
                  modifier={isPopUp() ? undefined : [BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
                  svgSpriteId={isPopUp() ? Icons.SHRINK_ARROWS : Icons.DECLINE_CROSS}
                  tooltipPlacement={isPopUp() ? TooltipPlacement.RIGHT : TooltipPlacement.TOP}
                  ariaDisabled={preventClose}
                  onClick={attemptClose}
                  aria-controls="modal-dialog"
                />
              </div>
            </div>
            <div className="modal-dialog__body">
              <ModalContext.Provider value={modalContextValue}>
                {typeof children === 'function' ? children(focusRef) : children}
              </ModalContext.Provider>
            </div>
          </div>
        </div>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div className="modal-dialog__overlay" onTouchStart={attemptClose} onMouseDown={attemptClose} />
      </div>
    </Portal>
  );
}
